import { type ReactNode } from "react";
import { ContentFieldType } from "../../../types/cms/contentField.type";
import { DynamicDateTime } from "../../../components/DynamicDateTime/DynamicDateTime";
import { DynamicRichText } from "../../../components/DynamicRichText/DynamicRichText";
import { createTokenRegex } from "../internal/constants";
import { transformRelationsToFlat } from "../internal/flatten";
import { resolveConditionals } from "../internal/resolveConditionals";
import { interpolateString } from "./interpolateString";
import type { InterpolateOptions, InterpolateParams } from "../types";

function resolvePath(structure: any, path: string): unknown {
  const keys = path.split(".");
  let value: any = structure;
  for (const key of keys) {
    if (value == null || typeof value !== "object") return undefined;
    value = value[key];
  }
  return value;
}

/**
 * Interpola tokens `{{path|type}}` num template e retorna **ReactNode**.
 *
 * Para tokens `|datetime`, emite `<DynamicDateTime />` (formata no fuso
 * do browser pós-hidratação). Para tokens `|rich_text`, emite
 * `<DynamicRichText />` (parseia HTML em ReactNode real, sem
 * `dangerouslySetInnerHTML`). Para qualquer outro tipo, delega ao
 * `interpolateString` aplicado ao token isolado e empurra a string
 * resultante.
 *
 * Com `options.parseHtml = true`, tokens não-especiais também passam pelo
 * `<DynamicRichText />` (string crua é parseada como HTML). Útil para
 * consumers cujos templates legados usam `|text` em campos com HTML.
 *
 * Normalmente acessada via `interpolate(...).node`.
 */
export function interpolateNodes(
  { structure, template }: InterpolateParams,
  options?: InterpolateOptions,
): ReactNode {
  const { parseHtml = false } = options || {};

  const flatStructure = transformRelationsToFlat(structure);
  const resolvedTemplate = resolveConditionals({ template, structure: flatStructure });
  const parts: Array<string | ReactNode> = [];
  let cursor = 0;
  let hasReactNode = false;

  const pushLiteral = (value: string, at: number) => {
    if (!value) return;

    if (parseHtml) {
      parts.push(<DynamicRichText key={`literal-${at}`} html={value} />);
      hasReactNode = true;
      return;
    }

    parts.push(value);
  };

  const regex = createTokenRegex();
  let match: RegExpExecArray | null;
  while ((match = regex.exec(resolvedTemplate)) !== null) {
    const [fullMatch, path, type] = match;
    const matchIndex = match.index;

    if (matchIndex > cursor) {
      pushLiteral(resolvedTemplate.slice(cursor, matchIndex), cursor);
    }
    cursor = matchIndex + fullMatch.length;

    if (type === ContentFieldType.RICH_TEXT) {
      const value = resolvePath(flatStructure, path);
      if (value != null && value !== "") {
        parts.push(
          <DynamicRichText
            key={matchIndex}
            html={String(value)}
          />,
        );
        hasReactNode = true;
      }

      continue;
    }

    if (type === ContentFieldType.DATETIME) {
      const value = resolvePath(flatStructure, path);
      if (value != null && value !== "") {
        const iso = value instanceof Date ? value.toISOString() : String(value);
        parts.push(
          <DynamicDateTime
            key={matchIndex}
            value={iso}
          />,
        );
        hasReactNode = true;
      }
      continue;
    }

    const stringOutput = interpolateString({
      structure: flatStructure,
      template: fullMatch,
    });
    if (parseHtml) {
      parts.push(
        <DynamicRichText
          key={matchIndex}
          html={stringOutput}
        />,
      );
      hasReactNode = true;
    } else {
      parts.push(stringOutput);
    }
  }

  if (cursor < resolvedTemplate.length) {
    pushLiteral(resolvedTemplate.slice(cursor), cursor);
  }

  if (hasReactNode) return <>{parts}</>;

  return parts.join("");
}
